import { useCallback, useMemo, useState } from 'react'
import { Box, Card, Group, SegmentedControl, Stack, Text } from '@mantine/core'
import { Chart } from 'react-chartjs-2'
import type { ChartData, ChartDataset, ChartOptions } from 'chart.js'
import {
  ANALYTICS_TOOLTIP_MAX_HEIGHT,
  ANALYTICS_TOOLTIP_PADDING,
  ANALYTICS_TOOLTIP_WIDTH,
  buildHourlyAxis,
  changeLine,
  compactChangeLine,
  compactSummaryText,
  formatHourLabel,
  formatHourRange,
  formatNumber,
  hourlyFallbackColors,
  withAlpha,
} from '@/pages/analytics/analytics-new-utils'
import {
  AnalyticsAiToggle,
  AnalyticsInlineToggle,
  AnalyticsTooltipContent,
  BlockLoader,
  ChartInfoBlock,
} from '@/pages/analytics/analytics-new-widgets'
import type { AnalyticsDashboardStats, ProjectRow } from '@/types/api'

function HourlyProductTag({ name, color, selected, onClick }: { name: string; color: string; selected: boolean; onClick: () => void }) {
  return (
    <Group
      component="button"
      gap={8}
      wrap="nowrap"
      onClick={onClick}
      style={{
        border: selected ? '1px solid color-mix(in srgb, var(--border-color) 82%, #4F86F7)' : '1px solid color-mix(in srgb, var(--border-color) 86%, white)',
        background: selected ? withAlpha(color, 0.14) : 'color-mix(in srgb, var(--bg-tertiary) 68%, white)',
        borderRadius: 999,
        padding: '8px 12px',
        cursor: 'pointer',
        opacity: selected ? 1 : 0.72,
      }}
    >
      <Box w={10} h={10} style={{ borderRadius: 999, background: color, flexShrink: 0 }} />
      <Text style={{ fontSize: 13, lineHeight: '16px', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{name}</Text>
    </Group>
  )
}

export type HourlyBasis = 'created' | 'closed'

const HOURLY_BASIS_OPTIONS: Array<{ label: string; value: HourlyBasis }> = [
  { label: 'Входящие', value: 'created' },
  { label: 'Закрытые', value: 'closed' },
]

export function HourlyDistributionCard({
  stats,
  prevStats,
  projects,
  loading,
  withoutAI,
  onWithoutAIChange,
  basis,
  onBasisChange,
}: {
  stats: AnalyticsDashboardStats | null
  prevStats: AnalyticsDashboardStats | null
  projects: ProjectRow[]
  loading: boolean
  withoutAI: boolean
  onWithoutAIChange: (checked: boolean) => void
  basis: HourlyBasis
  onBasisChange: (basis: HourlyBasis) => void
}) {
  const [showPrevious, setShowPrevious] = useState(true)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [hourlyTooltip, setHourlyTooltip] = useState<{
    visible: boolean
    x: number
    y: number
    chartWidth: number
    chartHeight: number
    title: string
    currentTotal: number
    previousTotal: number
    rows: Array<{ projectId: string; name: string; color: string; current: number; previous: number; percent: number }>
    hiddenRows: number
  }>({
    visible: false,
    x: 0,
    y: 0,
    chartWidth: 0,
    chartHeight: 0,
    title: '',
    currentTotal: 0,
    previousTotal: 0,
    rows: [],
    hiddenRows: 0,
  })

  const hourlyRows = useMemo(() => {
    const rows = stats?.tickets_by_hour_by_project ?? []
    return rows
      .map((row, index) => {
        const project = projects.find((item) => item.id === row.project_id)
        const color = project?.color || hourlyFallbackColors[index % hourlyFallbackColors.length]
        const values = Array.from({ length: 24 }, (_, hour) => Number(row.hourly_data?.[String(hour)] ?? row.hourly_data?.[hour] ?? 0))
        return {
          projectId: row.project_id,
          name: project?.name || row.project_name || '—',
          color,
          values,
          total: values.reduce((sum, value) => sum + value, 0),
        }
      })
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total)
  }, [projects, stats?.tickets_by_hour_by_project])

  const hourlyTotals = useMemo(
    () => Array.from({ length: 24 }, (_, hour) => Number(stats?.tickets_by_hour?.[String(hour)] ?? stats?.tickets_by_hour?.[hour] ?? 0)),
    [stats?.tickets_by_hour],
  )
  const prevHourlyTotals = useMemo(
    () => Array.from({ length: 24 }, (_, hour) => Number(prevStats?.tickets_by_hour?.[String(hour)] ?? prevStats?.tickets_by_hour?.[hour] ?? 0)),
    [prevStats?.tickets_by_hour],
  )
  const prevHourlyRowsByProject = useMemo(() => {
    const rows = prevStats?.tickets_by_hour_by_project ?? []
    return new Map(
      rows.map((row) => [
        row.project_id,
        Array.from({ length: 24 }, (_, hour) => Number(row.hourly_data?.[String(hour)] ?? row.hourly_data?.[hour] ?? 0)),
      ]),
    )
  }, [prevStats?.tickets_by_hour_by_project])

  const totalTickets = hourlyTotals.reduce((sum, value) => sum + value, 0)
  const prevTotalTickets = prevHourlyTotals.reduce((sum, value) => sum + value, 0)
  const averagePerHour = totalTickets > 0 ? totalTickets / 24 : 0
  const prevAveragePerHour = prevTotalTickets > 0 ? prevTotalTickets / 24 : 0
  const peakHourIndex = hourlyTotals.reduce((best, value, index, arr) => (value > arr[best] ? index : best), 0)
  const minHourIndex = hourlyTotals.reduce((best, value, index, arr) => (value < arr[best] ? index : best), 0)
  const maxHourIndex = hourlyTotals.reduce((best, value, index, arr) => (value > arr[best] ? index : best), 0)
  const hourlyDelta = changeLine(totalTickets, prevTotalTickets, true)
  const hourlyDynamic = compactChangeLine(totalTickets, prevTotalTickets, true)
  const volumeTone = totalTickets < prevTotalTickets ? 'good' : totalTickets > prevTotalTickets ? 'bad' : 'neutral'
  const avgTone = averagePerHour < prevAveragePerHour ? 'good' : averagePerHour > prevAveragePerHour ? 'bad' : 'neutral'
  const changeTone = totalTickets < prevTotalTickets ? 'good' : totalTickets > prevTotalTickets ? 'bad' : 'neutral'
  const orderedHourlyRows = useMemo(() => {
    if (!selectedProjectId) return hourlyRows
    const selected = hourlyRows.find((row) => row.projectId === selectedProjectId)
    if (!selected) return hourlyRows
    return [selected, ...hourlyRows.filter((row) => row.projectId !== selectedProjectId)]
  }, [hourlyRows, selectedProjectId])
  const maxStackedValue = hourlyTotals.reduce((max, value) => Math.max(max, value), 0)
  const maxPreviousValue = showPrevious ? prevHourlyTotals.reduce((max, value) => Math.max(max, value), 0) : 0
  const axis = buildHourlyAxis(Math.max(maxStackedValue, maxPreviousValue))

  const chartData = useMemo<ChartData<'bar' | 'line'>>(() => {
    const labels = Array.from({ length: 24 }, (_, hour) => formatHourLabel(hour))
    const datasets: ChartDataset<'bar' | 'line'>[] = orderedHourlyRows.map((row) => {
      const hasSelection = selectedProjectId != null
      const isSelected = selectedProjectId === row.projectId
      const mutedFill = 'rgba(148, 163, 184, 0.13)'
      return {
        type: 'bar' as const,
        label: row.name,
        data: row.values,
        stack: 'hourly-current',
        backgroundColor: hasSelection ? (isSelected ? withAlpha(row.color, 0.96) : mutedFill) : withAlpha(row.color, 0.82),
        hoverBackgroundColor: hasSelection ? (isSelected ? row.color : 'rgba(148, 163, 184, 0.22)') : withAlpha(row.color, 0.96),
        borderColor: hasSelection ? (isSelected ? withAlpha(row.color, 1) : 'rgba(203, 213, 225, 0.34)') : 'rgba(255, 255, 255, 0.72)',
        borderWidth: hasSelection ? (isSelected ? 1.5 : 1) : 0,
        borderRadius: hasSelection && !isSelected ? 4 : 9,
        borderSkipped: false,
        borderJoinStyle: 'round',
        minBarLength: 2,
        order: 2,
      }
    })
    if (showPrevious) {
      datasets.push({
        type: 'line',
        label: 'Предыдущий период',
        data: prevHourlyTotals,
        borderColor: selectedProjectId ? 'rgba(148, 163, 184, 0.56)' : 'rgba(148, 163, 184, 0.82)',
        backgroundColor: 'rgba(148, 163, 184, 0.10)',
        borderDash: [6, 6],
        borderWidth: selectedProjectId ? 2 : 3,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.35,
        fill: false,
        order: 1,
      })
    }
    return { labels, datasets }
  }, [orderedHourlyRows, prevHourlyTotals, selectedProjectId, showPrevious])

  const chartOptions = useMemo<ChartOptions<'bar' | 'line'>>(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: (context) => {
          const tooltip = context.tooltip
          if (!tooltip || tooltip.opacity === 0) {
            setHourlyTooltip((current) => (current.visible ? { ...current, visible: false } : current))
            return
          }
          const hour = tooltip.dataPoints?.[0]?.dataIndex ?? 0
          const allRows = orderedHourlyRows
            .map((row) => {
              const current = Number(row.values[hour] ?? 0)
              const previous = Number(prevHourlyRowsByProject.get(row.projectId)?.[hour] ?? 0)
              const total = Number(hourlyTotals[hour] ?? 0)
              return {
                projectId: row.projectId,
                name: row.name,
                color: row.color,
                current,
                previous,
                percent: total > 0 ? (current / total) * 100 : 0,
              }
            })
            .sort((a, b) => b.current - a.current)
          setHourlyTooltip({
            visible: true,
            x: tooltip.caretX,
            y: tooltip.caretY,
            chartWidth: context.chart.width,
            chartHeight: context.chart.height,
            title: formatHourRange(hour),
            currentTotal: Number(hourlyTotals[hour] ?? 0),
            previousTotal: Number(prevHourlyTotals[hour] ?? 0),
            rows: allRows.slice(0, 8),
            hiddenRows: Math.max(0, allRows.length - 8),
          })
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: '#475569',
          font: { size: 12 },
          maxRotation: 0,
          minRotation: 0,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: axis.max,
        ticks: {
          stepSize: axis.step,
          color: '#475569',
          font: { size: 12 },
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.38)',
          lineWidth: 1,
        },
        border: { display: false },
      },
    },
  }), [axis.max, axis.step, hourlyTotals, orderedHourlyRows, prevHourlyRowsByProject, prevHourlyTotals])

  const toggleProject = useCallback((projectId: string) => {
    setSelectedProjectId((current) => (current === projectId ? null : projectId))
  }, [])

  const hourlyTooltipWidth = ANALYTICS_TOOLTIP_WIDTH
  const hourlyTooltipHeight = Math.min(ANALYTICS_TOOLTIP_MAX_HEIGHT, 74 + hourlyTooltip.rows.length * 31 + (hourlyTooltip.hiddenRows > 0 ? 24 : 0))
  const hourlyTooltipLeft = Math.max(8, Math.min(hourlyTooltip.x + 14, Math.max(8, hourlyTooltip.chartWidth - hourlyTooltipWidth - 8)))
  const hourlyTooltipTop = Math.max(8, Math.min(hourlyTooltip.y - hourlyTooltipHeight / 2, Math.max(8, hourlyTooltip.chartHeight - hourlyTooltipHeight - 8)))

  return (
    <Card withBorder radius={18} padding={20} pos="relative" style={{ borderColor: 'var(--border-color)', background: 'var(--bg-primary)', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)', overflow: 'hidden' }}>
      {loading ? <BlockLoader label="Пересчитываем распределение по часам" /> : null}
      <Stack gap={18} opacity={loading ? 0.45 : 1}>
        <Group justify="space-between" align="flex-start" gap="md" wrap="wrap">
          <Stack gap={6}>
            <Text fw={700} style={{ fontSize: 16, lineHeight: '22px', letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>Распределение обращений по часам</Text>
            <Text size="xs" c="dimmed">
              {basis === 'created' ? 'Входящий поток: час создания обращения' : 'Закрытые обращения: час закрытия'}
            </Text>
          </Stack>
          <Group gap={16} align="center" wrap="wrap" justify="flex-end">
            <SegmentedControl size="xs" value={basis} onChange={(value) => onBasisChange(value as HourlyBasis)} data={HOURLY_BASIS_OPTIONS} />
            <AnalyticsInlineToggle checked={showPrevious} onChange={setShowPrevious} label="Показать предыдущий период" />
            <AnalyticsAiToggle checked={withoutAI} onChange={onWithoutAIChange} />
          </Group>
        </Group>

        <Group gap={8} wrap="wrap">
          <ChartInfoBlock
            label="Динамика"
            value={hourlyDynamic?.text ?? compactSummaryText(hourlyDelta)}
            tone={changeTone}
            tooltip={selectedProjectId ? 'Выбранный продукт показан внизу столбцов, остальные остаются сверху в приглушенном виде.' : undefined}
          />
          <ChartInfoBlock label="Всего обращений" value={formatNumber(totalTickets)} tone={volumeTone} />
          <ChartInfoBlock label="Предыдущий период" value={formatNumber(prevTotalTickets)} />
          <ChartInfoBlock
            label="Среднее в час"
            value={formatNumber(Math.round(averagePerHour))}
            tone={avgTone}
            tooltip={
              <AnalyticsTooltipContent
                title="Среднее в час"
                rows={[
                  { label: 'Минимум', value: `${formatNumber(hourlyTotals[minHourIndex] ?? 0)} (${formatHourRange(minHourIndex)})` },
                  { label: 'Максимум', value: `${formatNumber(hourlyTotals[maxHourIndex] ?? 0)} (${formatHourRange(maxHourIndex)})`, tone: 'primary' },
                ]}
              />
            }
          />
          <ChartInfoBlock label="Пик нагрузки" value={formatHourLabel(peakHourIndex)} />
        </Group>

        {hourlyRows.length === 0 ? (
          <Text c="dimmed" size="sm">Нет данных для почасового распределения.</Text>
        ) : (
          <Stack gap={18}>
            <Box style={{ height: 430, position: 'relative' }}>
              <Chart type="bar" data={chartData} options={chartOptions} />
              {hourlyTooltip.visible ? (
                <Box
                  style={{
                    position: 'absolute',
                    left: hourlyTooltipLeft,
                    top: hourlyTooltipTop,
                    width: hourlyTooltipWidth,
                    maxWidth: 'calc(100% - 24px)',
                    borderRadius: 12,
                    background: '#303030',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    boxShadow: '0 16px 36px rgba(0, 0, 0, 0.28)',
                    padding: ANALYTICS_TOOLTIP_PADDING,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }}
                >
                  <Stack gap={10}>
                    <Stack gap={3}>
                      <Text style={{ fontSize: 12, lineHeight: '15px', fontWeight: 700, color: '#ffffff' }}>{hourlyTooltip.title}</Text>
                      {showPrevious ? (
                        <Group justify="space-between" gap={12} wrap="nowrap" style={{ minHeight: 24, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                          <Text style={{ fontSize: 11, lineHeight: '14px', color: '#C4CBD5' }}>Предыдущий период</Text>
                          <Text style={{ fontSize: 12, lineHeight: '15px', color: '#C4CBD5', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(hourlyTooltip.previousTotal)}</Text>
                        </Group>
                      ) : null}
                      <Group justify="space-between" gap={12} wrap="nowrap" style={{ minHeight: 24, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                        <Text style={{ fontSize: 11, lineHeight: '14px', color: '#F8FAFC' }}>Текущий период</Text>
                        <Text style={{ fontSize: 12, lineHeight: '15px', color: '#5B95FF', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatNumber(hourlyTooltip.currentTotal)}</Text>
                      </Group>
                    </Stack>

                    <Stack gap={0}>
                      {hourlyTooltip.rows.map((row) => (
                        <Group
                          key={`${row.projectId}-${hourlyTooltip.title}`}
                          justify="space-between"
                          gap={8}
                          wrap="nowrap"
                          style={{
                            minHeight: 30,
                            borderBottom: '1px solid rgba(255,255,255,0.07)',
                          }}
                        >
                          <Group gap={8} wrap="nowrap" style={{ minWidth: 0, flex: '1 1 auto' }}>
                            <Box w={16} h={16} style={{ borderRadius: 999, background: row.color, flexShrink: 0 }} />
                            <Text truncate style={{ minWidth: 0, fontSize: 12, lineHeight: '15px', fontWeight: 600, color: '#ffffff' }}>{row.name}</Text>
                          </Group>
                          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                            <Text style={{ fontSize: 12, lineHeight: '15px', fontWeight: 700, color: '#ffffff' }}>{formatNumber(row.current)}</Text>
                            {showPrevious ? (
                              <Text style={{ fontSize: 11, lineHeight: '14px', fontWeight: 700, color: '#60A5FA' }}>{formatNumber(row.previous)}</Text>
                            ) : null}
                            <Text style={{ fontSize: 11, lineHeight: '14px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.78)' }}>({Math.round(row.percent)}%)</Text>
                          </Group>
                        </Group>
                      ))}
                      {hourlyTooltip.hiddenRows > 0 ? (
                        <Text style={{ paddingTop: 6, fontSize: 11, lineHeight: '14px', fontWeight: 700, color: '#C4CBD5' }}>
                          Еще {formatNumber(hourlyTooltip.hiddenRows)}
                        </Text>
                      ) : null}
                    </Stack>
                  </Stack>
                </Box>
              ) : null}
            </Box>

            <Group gap={10} wrap="wrap" justify="center">
              {hourlyRows.map((row) => (
                <HourlyProductTag
                  key={row.projectId}
                  name={row.name}
                  color={row.color}
                  selected={selectedProjectId === row.projectId}
                  onClick={() => toggleProject(row.projectId)}
                />
              ))}
            </Group>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
